import React from "react";
import styled from "@emotion/styled";
import { dataViz } from "@atlas-int/tokens";

// Aspect ratio from the SVG viewBox (7.9 x 13.6)
const aspectRatio = 13.6 / 7.9;
const defaultArrowWidth = 10; // px

const arrowActiveColor = dataViz.neutral;
const arrowInactiveColor = dataViz.unfocused;
const inactiveOpacity = 0.2;
const activeOpacity = 1;

export interface SortIndicatorProps {
  direction: "asc" | "desc" | "none";
  active: boolean;
  /** Arrow width in px (default 10) */
  size?: number;
  className?: string;
}

const Container = styled.span<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $width }) => Math.round($width * aspectRatio)}px;
  position: relative;
  margin-left: 0.5rem;
  display: inline-flex;
  flex-shrink: 0;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: ${({ $width }) => $width}px;
    fill-opacity: var(--sort-indicator-opacity);

    polygon:first-of-type {
      fill: var(--sort-indicator-up-color);
    }

    polygon:last-of-type {
      fill: var(--sort-indicator-down-color);
    }
  }

  @media (max-width: 1050px) {
    width: 7px;
    height: ${Math.round(7 * aspectRatio)}px;

    svg {
      width: 7px;
    }
  }
`;

// Inline SVG — same geometry as sorting-icon.svg
const SortingSvg = () => (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 7.9 13.6"
  >
    <g>
      <polygon points="4,0 0.1,5.8 7.8,5.8" />
      <polygon points="4,13.5 7.8,7.8 0.1,7.8" />
    </g>
  </svg>
);

const SortIndicator: React.FC<SortIndicatorProps> = ({
  direction,
  active,
  size = defaultArrowWidth,
  className,
}) => {
  let upColor: string;
  let downColor: string;
  let opacity: number;

  if (active) {
    opacity = activeOpacity;
    upColor =
      direction === "desc" ? arrowActiveColor : arrowInactiveColor;
    downColor =
      direction === "asc" ? arrowActiveColor : arrowInactiveColor;
  } else {
    opacity = inactiveOpacity;
    upColor = arrowActiveColor;
    downColor = arrowActiveColor;
  }

  const style: React.CSSProperties & Record<string, unknown> = {
    "--sort-indicator-up-color": upColor,
    "--sort-indicator-down-color": downColor,
    "--sort-indicator-opacity": opacity,
  };

  return (
    <Container $width={size} style={style} className={className}>
      <SortingSvg />
    </Container>
  );
};

export default SortIndicator;
